<template>
  <div class="loc">
    <location-form
      ref="form"
      :key="formKey"
      v-model="location"
      :readonly="!canEditLocation"
      @edited="revision += 1"
    />

    <ds-save-bar v-if="canEditLocation">
      <template
        v-if="isDirty"
        #status
      >
        Unsaved changes
      </template>
      <ds-button
        variant="secondary"
        :disabled="saving"
        @click="discard"
      >
        Discard
      </ds-button>
      <ds-button
        :disabled="saving"
        @click="save"
      >
        {{ saving ? 'Saving…' : 'Save changes' }}
      </ds-button>
    </ds-save-bar>
    <p
      v-else
      class="loc-readonly"
    >
      Only organization admins can edit these details.
    </p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { DsButton, DsSaveBar } from '@/design-system/components'
import LocationForm from '@/components/account/LocationForm.vue'

// The fields the form edits. Snapshotted so Discard can put them back.
const EDITABLE = [
  'name', 'description', 'shortDescription', 'phone', 'email',
  'website', 'address', 'postalCode', 'cityId',
]

const cloneTranslations = translations => JSON.parse(JSON.stringify(translations || {}))

// Comparable form of the translations map. Order-independent, and blank values are ignored
// because Translateable seeds an empty entry for every language on mount.
const translationSignature = translations => {
  const entries = []
  Object.entries(translations || {}).forEach(([language, fields]) => {
    Object.entries(fields || {}).forEach(([field, text]) => {
      if (text !== '' && text != null) entries.push(`${language}.${field}=${text}`)
    })
  })
  return entries.sort().join('|')
}

export default {
  name: 'Location',
  components: {
    DsButton,
    DsSaveBar,
    LocationForm,
  },
  data() {
    return {
      saving: false,
      // Last persisted values. Discard reverts to this, not to the original page load,
      // so discarding after a save returns to what's actually on the server.
      baseline: null,
      // Bumped on discard to remount the form, so the embedded Translateable and
      // CitySelect re-read the reverted location instead of holding stale copies.
      formKey: 0,
      // Translateable writes into nested keys Vue 2 can't track, so it tells us when it
      // edited and this re-triggers isDirty.
      revision: 0,
    }
  },
  computed: {
    ...mapGetters({
      location: 'authentication/location',
      canEditLocation: 'authentication/canEditLocation',
    }),
    // Whether anything differs from what's persisted.
    isDirty() {
      if (!this.baseline) return false
      // Referenced so a translation edit re-runs this.
      this.revision // eslint-disable-line no-unused-expressions
      const fieldChanged = EDITABLE.some(field => this.location[field] !== this.baseline[field])
      return fieldChanged
        || translationSignature(this.location.translations) !== translationSignature(this.baseline.translations)
    },
  },
  watch: {
    // `location` resolves to {} until the partner loads; snapshot once it's real.
    'location.id': {
      immediate: true,
      handler() {
        this.captureBaseline()
      },
    },
  },
  methods: {
    captureBaseline() {
      if (!this.location || !this.location.id) return
      const snapshot = { translations: cloneTranslations(this.location.translations) }
      EDITABLE.forEach(field => {
        snapshot[field] = this.location[field]
      })
      this.baseline = snapshot
    },
    discard() {
      if (this.saving || !this.baseline) return
      EDITABLE.forEach(field => {
        this.$set(this.location, field, this.baseline[field])
      })
      this.$set(this.location, 'translations', cloneTranslations(this.baseline.translations))
      this.formKey += 1
    },
    save() {
      if (this.saving) return undefined
      // Don't send contact details we know won't work.
      const invalid = this.$refs.form.validate()
      if (invalid) {
        this.$notify.blocked(invalid)
        return undefined
      }
      this.saving = true
      return this.location.update().then(val => {
        // What's on screen is now persisted, so it becomes the new discard target.
        this.captureBaseline()
        this.$notify.success(val.data.message)
      }).catch(error => {
        // A network failure arrives without errorData; reading it blindly threw in the catch.
        const reason = (error && error.errorData && error.errorData.message)
          || (error && error.message)
          || 'something went wrong'
        this.$notify.error(`Failed with reason: ${reason}`)
      }).finally(() => {
        this.saving = false
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/design-system/mixins';

.loc-readonly {
  margin: 14px 0 0;
  font-size: 12px;
  font-style: italic;
  color: $c-text-sec;
}
</style>
